import { apiResponseType } from "@/Api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { format, subDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon, Filter, Trash2, X } from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { getSelectPaymentForms } from "@/Api/paymentForms";
import { getSelectUnitiesOnlyActives } from "@/Api/unities";
import { selectRevenueCategoriesData } from "@/Api/revenueCategories";
import { selectExpenseCategoriesData } from "@/Api/expenseCategories";
import { selectExpenseTypesData } from "@/Api/expenseTypes";
import { Mask } from "@/utils/mask";

const FINANCIAL_TRANSACTION_STATUS = {
  OPEN: 1,
  PAID: 2,
  CANCELED: 3,
  EXPIRED: 4,
};

const TRANSACTION_STATUS = [
  { id: FINANCIAL_TRANSACTION_STATUS.OPEN, name: "Em Aberto" },
  { id: FINANCIAL_TRANSACTION_STATUS.PAID, name: "Pago" },
  { id: FINANCIAL_TRANSACTION_STATUS.CANCELED, name: "Cancelado" },
  { id: FINANCIAL_TRANSACTION_STATUS.EXPIRED, name: "Vencido" },
];

export type FilterProps = {
  updateFront: (response: any) => void;
  setState: Dispatch<SetStateAction<string>>;
  funcSearch: (params: any) => apiResponseType;
  transactionType?: number;
};

type FilterItem = {
  key: string;
  label: string;
  value: string;
};

const TRANSACTION_TYPES = {
  ACCOUNTS_PAYABLE: 1,
  ACCOUNTS_RECEIVABLE: 2,
};

const PERIOD_OPTIONS = [
  { label: "Hoje", days: 0 },
  { label: "Últimos 7 dias", days: 7 },
  { label: "Últimos 30 dias", days: 30 },
  { label: "Este mês", days: "current-month" },
  { label: "Personalizado", days: "custom" },
];

export const TransactionFilters = ({
  updateFront,
  setState,
  funcSearch,
  transactionType: initialTransactionType,
}: FilterProps) => {
  const today = new Date();
  const [paymentDateStart, setPaymentDateStart] = useState<Date>(
    subDays(today, 7)
  );
  const [paymentDateEnd, setPaymentDateEnd] = useState<Date>(today);
  const [dueDateStart, setDueDateStart] = useState<Date>(subDays(today, 7));
  const [dueDateEnd, setDueDateEnd] = useState<Date>(today);
  const [createdDateStart, setCreatedDateStart] = useState<Date>(
    subDays(today, 7)
  );
  const [createdDateEnd, setCreatedDateEnd] = useState<Date>(today);
  const [competenceDateStart, setCompetenceDateStart] = useState<Date>(
    subDays(today, 7)
  );
  const [competenceDateEnd, setCompetenceDateEnd] = useState<Date>(today);

  const [transactionType, setTransactionType] = useState<number | undefined>(
    initialTransactionType
  );
  const [transactionTypeValue, setTransactionTypeValue] = useState<string>(
    initialTransactionType ? initialTransactionType.toString() : "all"
  );
  const [periodOption, setPeriodOption] = useState("7");
  const [isCustomPeriod, setIsCustomPeriod] = useState(false);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState<FilterItem[]>([]);

  const [description, setDescription] = useState<string>("");
  const [amount, setAmount] = useState<string>("");
  const [expenseId, setExpenseId] = useState<string>("none");
  const [paymentFormId, setPaymentFormId] = useState<string>("none");
  const [paymentForms, setPaymentForms] = useState<any[]>([]);
  const [unityId, setUnityId] = useState<string>("none");
  const [unities, setUnities] = useState<any[]>([]);
  const [revenueCategoryId, setRevenueCategoryId] = useState<string>("none");
  const [revenueCategories, setRevenueCategories] = useState<any[]>([]);
  const [expenseCategoryId, setExpenseCategoryId] = useState<string>("none");
  const [expenseCategories, setExpenseCategories] = useState<any[]>([]);
  const [expenseTypes, setExpenseTypes] = useState<any[]>([]);
  const [statusId, setStatusId] = useState<string>("none");
  const [activeDateFilter, setActiveDateFilter] = useState<
    "payment" | "due" | "created" | "competence" | "none"
  >("payment");

  useEffect(() => {
    setTransactionType(initialTransactionType);
    setTransactionTypeValue(
      initialTransactionType ? initialTransactionType.toString() : "all"
    );

    if (initialTransactionType === TRANSACTION_TYPES.ACCOUNTS_RECEIVABLE) {
      setExpenseId("none");
      setActiveDateFilter("payment");
    }
  }, [initialTransactionType]);

  useEffect(() => {
    // Buscar formas de pagamento
    getSelectPaymentForms().then(({ data }) =>
      setPaymentForms(data?.response || [])
    );
    // Buscar unidades
    getSelectUnitiesOnlyActives().then(({ data }) => setUnities(data?.response || []));
    // Buscar categorias de receita
    selectRevenueCategoriesData().then(({ data }) =>
      setRevenueCategories(data?.response || [])
    );
    // Buscar categorias de despesa
    selectExpenseCategoriesData().then(({ data }) =>
      setExpenseCategories(data?.response || [])
    );

    selectExpenseTypesData().then(({ data }) => {
      setExpenseTypes(data?.response || []);
    });
  }, []);

  const updateActiveFilters = () => {
    const newFilters: FilterItem[] = [];

    if (transactionType) {
      const label =
        transactionType === TRANSACTION_TYPES.ACCOUNTS_PAYABLE
          ? "Contas a Pagar"
          : "Contas a Receber";
      newFilters.push({
        key: "transaction_type_id",
        label: "Tipo",
        value: label,
      });
    }

    if (description) {
      newFilters.push({
        key: "description",
        label: "Descrição",
        value: description,
      });
    }

    if (amount) {
      newFilters.push({
        key: "amount",
        label: "Valor",
        value: amount,
      });
    }

    if (
      transactionType === TRANSACTION_TYPES.ACCOUNTS_PAYABLE &&
      expenseId &&
      expenseId !== "none"
    ) {
      newFilters.push({
        key: "expense_type_id",
        label: "Despesa",
        value: expenseId,
      });
    }

    if (paymentFormId && paymentFormId !== "none") {
      const pf = paymentForms.find((f) => f.id.toString() === paymentFormId);
      newFilters.push({
        key: "payment_form_id",
        label: "Forma de Pagamento",
        value: pf?.name || paymentFormId,
      });
    }
    if (unityId && unityId !== "none") {
      const u = unities.find((f) => f.id.toString() === unityId);
      newFilters.push({
        key: "unity_id",
        label: "Unidade",
        value: u?.name || unityId,
      });
    }
    if (
      transactionType === TRANSACTION_TYPES.ACCOUNTS_RECEIVABLE &&
      revenueCategoryId &&
      revenueCategoryId !== "none"
    ) {
      const cat = revenueCategories.find(
        (c) => c.id.toString() === revenueCategoryId
      );
      newFilters.push({
        key: "revenue_category_id",
        label: "Categoria Receita",
        value: cat?.name || revenueCategoryId,
      });
    }
    if (
      transactionType === TRANSACTION_TYPES.ACCOUNTS_PAYABLE &&
      expenseCategoryId &&
      expenseCategoryId !== "none"
    ) {
      const cat = expenseCategories.find(
        (c) => c.id.toString() === expenseCategoryId
      );
      newFilters.push({
        key: "expense_category_id",
        label: "Categoria Despesa",
        value: cat?.name || expenseCategoryId,
      });
    }

    if (statusId && statusId !== "none") {
      const status = TRANSACTION_STATUS.find(
        (s) => s.id.toString() === statusId
      );
      newFilters.push({
        key: "status_id",
        label: "Status",
        value: status?.name || statusId,
      });
    }

    if (activeDateFilter !== "none") {
      let periodLabel = "";
      if (activeDateFilter === "payment") {
        periodLabel = isCustomPeriod
          ? `${format(paymentDateStart, "dd/MM/yyyy", {
            locale: ptBR,
          })} a ${format(paymentDateEnd, "dd/MM/yyyy", { locale: ptBR })}`
          : PERIOD_OPTIONS.find((opt) => opt.days?.toString() === periodOption)
            ?.label || "Período";
        newFilters.push({
          key: "payment_date",
          label: "Data de Pagamento",
          value: periodLabel,
        });
      } else if (activeDateFilter === "due") {
        periodLabel = isCustomPeriod
          ? `${format(dueDateStart, "dd/MM/yyyy", { locale: ptBR })} a ${format(
            dueDateEnd,
            "dd/MM/yyyy",
            { locale: ptBR }
          )}`
          : PERIOD_OPTIONS.find((opt) => opt.days?.toString() === periodOption)
            ?.label || "Período";
        newFilters.push({
          key: "due_date",
          label: "Data de Vencimento",
          value: periodLabel,
        });
      } else if (activeDateFilter === "created") {
        periodLabel = isCustomPeriod
          ? `${format(createdDateStart, "dd/MM/yyyy", {
            locale: ptBR,
          })} a ${format(createdDateEnd, "dd/MM/yyyy", { locale: ptBR })}`
          : PERIOD_OPTIONS.find((opt) => opt.days?.toString() === periodOption)
            ?.label || "Período";
        newFilters.push({
          key: "created_date",
          label: "Data de Criação",
          value: periodLabel,
        });
      } else if (activeDateFilter === "competence") {
        periodLabel = isCustomPeriod
          ? `${format(competenceDateStart, "dd/MM/yyyy", {
            locale: ptBR,
          })} a ${format(competenceDateEnd, "dd/MM/yyyy", { locale: ptBR })}`
          : PERIOD_OPTIONS.find((opt) => opt.days?.toString() === periodOption)
            ?.label || "Período";
        newFilters.push({
          key: "competence_date",
          label: "Data de Competência",
          value: periodLabel,
        });
      }
    }

    setActiveFilters(newFilters);
    setHasActiveFilters(newFilters.length > 0);
  };

  const handlePeriodChange = (value: string) => {
    setPeriodOption(value);

    if (value === "custom") {
      setIsCustomPeriod(true);
      return;
    }

    setIsCustomPeriod(false);

    if (value === "current-month") {
      const firstDayOfMonth = new Date(
        today.getFullYear(),
        today.getMonth(),
        1
      );
      if (activeDateFilter === "payment") {
        setPaymentDateStart(firstDayOfMonth);
        setPaymentDateEnd(today);
      } else if (activeDateFilter === "due") {
        setDueDateStart(firstDayOfMonth);
        setDueDateEnd(today);
      } else if (activeDateFilter === "created") {
        setCreatedDateStart(firstDayOfMonth);
        setCreatedDateEnd(today);
      } else if (activeDateFilter === "competence") {
        setCompetenceDateStart(firstDayOfMonth);
        setCompetenceDateEnd(today);
      }
    } else {
      const days = parseInt(value, 10);
      if (activeDateFilter === "payment") {
        setPaymentDateStart(days > 0 ? subDays(today, days) : today);
        setPaymentDateEnd(today);
      } else if (activeDateFilter === "due") {
        setDueDateStart(days > 0 ? subDays(today, days) : today);
        setDueDateEnd(today);
      } else if (activeDateFilter === "created") {
        setCreatedDateStart(days > 0 ? subDays(today, days) : today);
        setCreatedDateEnd(today);
      } else if (activeDateFilter === "competence") {
        setCompetenceDateStart(days > 0 ? subDays(today, days) : today);
        setCompetenceDateEnd(today);
      }
    }
  };

  const handleTransactionTypeChange = (value: string) => {
    setTransactionTypeValue(value);

    if (value === "all") {
      setTransactionType(undefined);
    } else {
      setTransactionType(parseInt(value, 10));
      if (parseInt(value, 10) === TRANSACTION_TYPES.ACCOUNTS_RECEIVABLE) {
        setExpenseId("none");
        setActiveDateFilter("payment");
      }
    }
  };

  const applyFilters = async () => {
    setState("loading");
    updateActiveFilters();

    const filters: any = {};

    if (description) filters.description = description;

    const valueAmount = parseFloat(Mask.transformCurrencyInFloat(amount)) || null

    if (valueAmount) filters.amount = valueAmount;
    if (transactionType) filters.transaction_type_id = transactionType;

    if (transactionType === TRANSACTION_TYPES.ACCOUNTS_PAYABLE) {
      if (expenseId && expenseId !== "none")
        filters.expense_type_id = expenseId;

      if (activeDateFilter !== "none") {
        if (activeDateFilter === "payment") {
          filters.date_start_payment = format(paymentDateStart, "yyyy-MM-dd");
          filters.date_end_payment = format(paymentDateEnd, "yyyy-MM-dd");
        } else if (activeDateFilter === "due") {
          filters.date_start = format(dueDateStart, "yyyy-MM-dd");
          filters.date_end = format(dueDateEnd, "yyyy-MM-dd");
        } else if (activeDateFilter === "created") {
          filters.created_date_start = format(createdDateStart, "yyyy-MM-dd");
          filters.created_date_end = format(createdDateEnd, "yyyy-MM-dd");
        } else if (activeDateFilter === "competence") {
          filters.competence_date_start = format(
            competenceDateStart,
            "yyyy-MM-dd"
          );
          filters.competence_date_end = format(competenceDateEnd, "yyyy-MM-dd");
        }
      }
    } else if (transactionType === TRANSACTION_TYPES.ACCOUNTS_RECEIVABLE) {
      if (activeDateFilter === "competence") {
        filters.competence_date_start = format(
          competenceDateStart,
          "yyyy-MM-dd"
        );
        filters.competence_date_end = format(competenceDateEnd, "yyyy-MM-dd");
      } else if (activeDateFilter === "due") {
        filters.date_start = format(dueDateStart, "yyyy-MM-dd");
        filters.date_end = format(dueDateEnd, "yyyy-MM-dd");
      } else if (activeDateFilter === "created") {
        filters.created_date_start = format(createdDateStart, "yyyy-MM-dd");
        filters.created_date_end = format(createdDateEnd, "yyyy-MM-dd");
      } else {
        filters.date_start_payment = format(paymentDateStart, "yyyy-MM-dd");
        filters.date_end_payment = format(paymentDateEnd, "yyyy-MM-dd");
      }
    } else {
      if (activeDateFilter === "due") {
        filters.date_start = format(dueDateStart, "yyyy-MM-dd");
        filters.date_end = format(dueDateEnd, "yyyy-MM-dd");
      } else if (activeDateFilter === "created") {
        filters.created_date_start = format(createdDateStart, "yyyy-MM-dd");
        filters.created_date_end = format(createdDateEnd, "yyyy-MM-dd");
      } else if (activeDateFilter === "competence") {
        filters.competence_date_start = format(
          competenceDateStart,
          "yyyy-MM-dd"
        );
        filters.competence_date_end = format(competenceDateEnd, "yyyy-MM-dd");
      } else {
        filters.date_start_payment = format(paymentDateStart, "yyyy-MM-dd");
        filters.date_end_payment = format(paymentDateEnd, "yyyy-MM-dd");
      }
    }

    if (paymentFormId && paymentFormId !== "none")
      filters.payment_form_id = paymentFormId;
    if (unityId && unityId !== "none") filters.unity_id = unityId;
    if (
      transactionType === TRANSACTION_TYPES.ACCOUNTS_RECEIVABLE &&
      revenueCategoryId &&
      revenueCategoryId !== "none"
    )
      filters.revenue_category_id = revenueCategoryId;
    if (
      transactionType === TRANSACTION_TYPES.ACCOUNTS_PAYABLE &&
      expenseCategoryId &&
      expenseCategoryId !== "none"
    )
      filters.expense_category_id = expenseCategoryId;
    if (statusId && statusId !== "none") filters.status_id = statusId;

    try {
      const { data, ok } = await funcSearch(filters);
      if (ok) {
        updateFront(data?.response);
      }
    } catch (error) {
      console.error("Erro ao aplicar filtros:", error);
    } finally {
      setState("production");
    }
  };

  const clearFilters = async () => {
    setState("loading");
    setHasActiveFilters(false);
    setActiveFilters([]);

    if (!initialTransactionType) {
      setTransactionType(undefined);
      setTransactionTypeValue("all");
    }

    setPeriodOption("7");
    setIsCustomPeriod(false);
    setDescription("");
    setAmount("");
    setExpenseId("none");
    setActiveDateFilter("payment");
    setPaymentFormId("none");
    setUnityId("none");
    setRevenueCategoryId("none");
    setExpenseCategoryId("none");
    setStatusId("none");

    const defaultDays = 7;
    setPaymentDateStart(subDays(today, defaultDays));
    setPaymentDateEnd(today);
    setDueDateStart(subDays(today, defaultDays));
    setDueDateEnd(today);
    setCreatedDateStart(subDays(today, defaultDays));
    setCreatedDateEnd(today);
    setCompetenceDateStart(subDays(today, defaultDays));
    setCompetenceDateEnd(today);

    try {
      const baseFilters = initialTransactionType
        ? { transaction_type_id: initialTransactionType }
        : {};
      const { data } = await funcSearch(baseFilters);
      updateFront(data?.response);
    } catch (error) {
      console.error("Erro ao limpar filtros:", error);
    } finally {
      setState("production");
    }
  };

  const removeFilter = async (key: string) => {
    setState("loading");

    switch (key) {
      case "transaction_type_id":
        if (!initialTransactionType) {
          setTransactionType(undefined);
          setTransactionTypeValue("all");
        }
        break;
      case "description":
        setDescription("");
        break;
      case "amount":
        setAmount("");
        break;
      case "expense_type_id":
        setExpenseId("none");
        break;
      case "payment_date":
        setActiveDateFilter("payment");
        setPeriodOption("7");
        setIsCustomPeriod(false);
        setPaymentDateStart(subDays(today, 7));
        setPaymentDateEnd(today);
        break;
      case "due_date":
        setActiveDateFilter("due");
        setPeriodOption("7");
        setIsCustomPeriod(false);
        setDueDateStart(subDays(today, 7));
        setDueDateEnd(today);
        break;
      case "created_date":
        setActiveDateFilter("created");
        setPeriodOption("7");
        setIsCustomPeriod(false);
        setCreatedDateStart(subDays(today, 7));
        setCreatedDateEnd(today);
        break;
      case "payment_form_id":
        setPaymentFormId("none");
        break;
      case "unity_id":
        setUnityId("none");
        break;
      case "revenue_category_id":
        setRevenueCategoryId("none");
        break;
      case "expense_category_id":
        setExpenseCategoryId("none");
        break;
      case "status_id":
        setStatusId("none");
        break;
      case "competence_date":
        setActiveDateFilter("competence");
        setPeriodOption("7");
        setIsCustomPeriod(false);
        setCompetenceDateStart(subDays(today, 7));
        setCompetenceDateEnd(today);
        break;
    }

    const updatedFilters = activeFilters.filter((filter) => filter.key !== key);
    setActiveFilters(updatedFilters);
    setHasActiveFilters(updatedFilters.length > 0);

    applyFilters();
  };

  useEffect(() => {
    applyFilters();
  }, []);

  return (
    <div className="bg-white dark:bg-zinc-900 p-3 rounded-lg border shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium">Filtros</h3>
        {hasActiveFilters && (
          <Button
            size="sm"
            onClick={clearFilters}
            className="h-8 px-2 text-red-500 hover:text-red-600 hover:bg-red-500/10 bg-red-500/10"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Limpar
          </Button>
        )}
      </div>

      {hasActiveFilters && (
        <div className="mb-3 flex flex-wrap gap-1">
          {activeFilters.map((filter) => (
            <Badge
              key={filter.key}
              variant="secondary"
              className="flex items-center gap-1 py-1"
            >
              <span className="text-xs">
                {filter.label}: {filter.value}
              </span>
              <Button
                size="sm"
                onClick={() => removeFilter(filter.key)}
                className="h-4 w-4 p-0 ml-1"
                disabled={
                  filter.key === "transaction_type_id" &&
                  initialTransactionType !== undefined
                }
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}

      <Separator className="mb-3" />

      <div className="flex flex-wrap gap-3 mb-4">
        {!initialTransactionType && (
          <div className="flex flex-col gap-1 min-w-[140px]">
            <label className="text-xs text-muted-foreground">Tipo</label>
            <Select
              value={transactionTypeValue}
              onValueChange={handleTransactionTypeChange}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Todos os tipos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os tipos</SelectItem>
                <SelectItem
                  value={TRANSACTION_TYPES.ACCOUNTS_PAYABLE.toString()}
                >
                  Contas a Pagar
                </SelectItem>
                <SelectItem
                  value={TRANSACTION_TYPES.ACCOUNTS_RECEIVABLE.toString()}
                >
                  Contas a Receber
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
          <label className="text-xs text-muted-foreground">Descrição</label>
          <Input
            placeholder="Filtrar por descrição"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="h-9"
          />
        </div>

        <div className="flex flex-col gap-1 w-[120px]">
          <label className="text-xs text-muted-foreground">Valor</label>
          <Input
            placeholder="Valor"
            typeMask="currency"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="h-9"
          />
        </div>

        <div className="flex flex-col gap-1 min-w-[200px]">
          <label className="text-xs text-muted-foreground">
            Forma de Pagamento
          </label>
          <Select value={paymentFormId} onValueChange={setPaymentFormId}>
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Todas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Todas</SelectItem>
              {paymentForms.map((f) => (
                <SelectItem key={f.id} value={f.id.toString()}>
                  {f.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-1 min-w-[140px]">
          <label className="text-xs text-muted-foreground">Status</label>
          <Select value={statusId} onValueChange={setStatusId}>
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Todos</SelectItem>
              {TRANSACTION_STATUS.map((status) => (
                <SelectItem key={status.id} value={status.id.toString()}>
                  {status.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {(transactionType === TRANSACTION_TYPES.ACCOUNTS_PAYABLE ||
        initialTransactionType === TRANSACTION_TYPES.ACCOUNTS_PAYABLE) && (
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="flex flex-col gap-1 min-w-[200px]">
              <label className="text-xs text-muted-foreground">
                Tipo de Despesa
              </label>
              <Select value={expenseId} onValueChange={setExpenseId}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Todos os tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Todos os tipos</SelectItem>
                  {expenseTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id.toString()}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

      {(transactionType === TRANSACTION_TYPES.ACCOUNTS_PAYABLE ||
        initialTransactionType === TRANSACTION_TYPES.ACCOUNTS_PAYABLE) && (
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="flex flex-col gap-1 min-w-[200px]">
              <label className="text-xs text-muted-foreground">
                Categoria de Despesa
              </label>
              <Select
                value={expenseCategoryId}
                onValueChange={setExpenseCategoryId}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Todos as categorias" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Todos as categorias</SelectItem>
                  {expenseCategories.map((category) => (
                    <SelectItem key={category.id} value={category.id.toString()}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

      <div className="flex flex-col gap-1 min-w-[200px]">
        <label className="text-xs text-muted-foreground">
          Filtrar por data de
        </label>
        <Select
          value={activeDateFilter}
          onValueChange={(v) => setActiveDateFilter(v as any)}
        >
          <SelectTrigger className="h-9">
            <SelectValue placeholder="Tipo de data" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Nenhuma</SelectItem>
            <SelectItem value="payment">Data de Pagamento</SelectItem>
            <SelectItem value="due">Data de Vencimento</SelectItem>
            <SelectItem value="created">Data de Criação</SelectItem>
            <SelectItem value="competence">Data de Competência</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap gap-3 items-end mb-4">
        <div className="flex flex-col gap-1 min-w-[140px]">
          <label className="text-xs text-muted-foreground">Período</label>
          <Select value={periodOption} onValueChange={handlePeriodChange}>
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Selecionar período" />
            </SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map((option) => (
                <SelectItem
                  key={option.days?.toString()}
                  value={option.days?.toString() || "custom"}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isCustomPeriod && (
          <>
            <div className="flex flex-col gap-1 min-w-[140px]">
              <span className="text-xs text-muted-foreground">
                Data Inicial
              </span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal h-9",
                      !activeDateFilter && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {activeDateFilter === "payment"
                      ? format(paymentDateStart, "dd/MM/yyyy", { locale: ptBR })
                      : activeDateFilter === "due"
                        ? format(dueDateStart, "dd/MM/yyyy", { locale: ptBR })
                        : activeDateFilter === "created"
                          ? format(createdDateStart, "dd/MM/yyyy", { locale: ptBR })
                          : format(competenceDateStart, "dd/MM/yyyy", {
                            locale: ptBR,
                          })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={
                      activeDateFilter === "payment"
                        ? paymentDateStart
                        : activeDateFilter === "due"
                          ? dueDateStart
                          : activeDateFilter === "created"
                            ? createdDateStart
                            : competenceDateStart
                    }
                    onSelect={(date) => {
                      if (date) {
                        if (activeDateFilter === "payment") {
                          setPaymentDateStart(date);
                        } else if (activeDateFilter === "due") {
                          setDueDateStart(date);
                        } else if (activeDateFilter === "created") {
                          setCreatedDateStart(date);
                        } else if (activeDateFilter === "competence") {
                          setCompetenceDateStart(date);
                        }
                      }
                    }}
                    initialFocus
                    locale={ptBR}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex flex-col gap-1 min-w-[140px]">
              <span className="text-xs text-muted-foreground">Data Final</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal h-9",
                      !activeDateFilter && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {activeDateFilter === "payment"
                      ? format(paymentDateEnd, "dd/MM/yyyy", { locale: ptBR })
                      : activeDateFilter === "due"
                        ? format(dueDateEnd, "dd/MM/yyyy", { locale: ptBR })
                        : activeDateFilter === "created"
                          ? format(createdDateEnd, "dd/MM/yyyy", { locale: ptBR })
                          : format(competenceDateEnd, "dd/MM/yyyy", {
                            locale: ptBR,
                          })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={
                      activeDateFilter === "payment"
                        ? paymentDateEnd
                        : activeDateFilter === "due"
                          ? dueDateEnd
                          : activeDateFilter === "created"
                            ? createdDateEnd
                            : competenceDateEnd
                    }
                    onSelect={(date) => {
                      if (date) {
                        if (activeDateFilter === "payment") {
                          setPaymentDateEnd(date);
                        } else if (activeDateFilter === "due") {
                          setDueDateEnd(date);
                        } else if (activeDateFilter === "created") {
                          setCreatedDateEnd(date);
                        } else if (activeDateFilter === "competence") {
                          setCompetenceDateEnd(date);
                        }
                      }
                    }}
                    initialFocus
                    locale={ptBR}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </>
        )}
      </div>

      <Button onClick={applyFilters} className="w-full">
        <Filter className="h-4 w-4 mr-2" />
        Aplicar Filtros
      </Button>
    </div>
  );
};
